import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { useEffect, useRef, useState } from "react";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { createClient } from "@supabase/supabase-js";
import { Check, Loader2, X } from "lucide-react";
import { useAuthStep } from "@/hooks/useAuthStep";
import DOB from "@/components/DOB";


import { CLOUDFLARE_URL, SUPABASE_ANON_KEY, SUPABASE_URL } from "@/lib/config";
import { getAgeBand, trackEvent } from "@/lib/utils";

const formSchema = z.object({
	month: z.string().min(1, "Please select a month"),
	year: z.string().min(1, "Please select a year"),
});

const clientid = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { useAuth } from "@/lib/auth";
import { useCommunication } from "@/lib/CommunicationWrapper";

const GoogleAuth = () => {

	const { setAuthStep, uuid, setAgeBand, token, handleAuth, loginState } = useAuthStep();
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);
	const { updateOptions } = useCommunication();

	const { googleLogin } = useGoogleAuth();


	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			month: "",
			year: "",
		},
	});

	// Function to check if DOB is valid and age is 13+
	const isDOBValid = () => {
		const formData = form.getValues();
		const month = formData.month;
		const year = formData.year;

		// Check if both month and year are selected
		if (!month || !year) {
			return false;
		}
		return true;
	};

	// Watch form changes to update button state
	useEffect(() => {
		const subscription = form.watch((value) => {
			setIsButtonDisabled(!isDOBValid());
		});
		return () => subscription.unsubscribe();
	}, [form]);

	useEffect(() => {
		trackEvent("signup_google_loaded", { "distinct_id": uuid });
	}, []);

	const checkAgeRef = useRef<HTMLDivElement>(null);

	const checkAge = () => {

		if (isButtonDisabled) return;

		const { year, month } = form.getValues();

		if (new Date().getFullYear() - parseInt(year) < 13 || (new Date().getFullYear() - parseInt(year) === 13 && new Date().getMonth() + 1 < parseInt(month))) {
			setAuthStep("underAge");
			return;
		} else {
			checkAgeRef.current?.remove();
			setAgeBand(getAgeBand(parseInt(year)));
		}

	}

	const handleGoogleLogin = async (tokenResponse: CredentialResponse) => {
		const authFn = googleLogin(tokenResponse);
		handleAuth(authFn, "signup");
	}


	return (
		<GoogleOAuthProvider clientId={clientid}>
			<div className="px-[30px] text-gray-500 flex flex-col w-full">
				<h1 className="text-2xl font-semibold text-black mb-[5px]">Join now (takes 30 secs)</h1>
				<p>Over 1.5 million students made their Canvas better.</p>
				<div className="mt-[20px]">
					<Form {...form}>
						<form>
							<DOB form={form} />
							<div className={`mt-4 relative ${isButtonDisabled ? "opacity-50 pointer-events-none" : ""}`} >
								<div ref={checkAgeRef} className="w-full h-full absolute top-0 left-0 z-10 bg-none" onMouseOver={checkAge} />
								<GoogleLogin click_listener={checkAge} onSuccess={handleGoogleLogin} ux_mode="popup" type="standard" shape="rectangular" size="large" auto_select={false} />
								{loginState.loading && <span className="text-gray-500 text-sm flex items-center gap-2 mt-2"><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</span>}
								{loginState.success && <span className="text-green-700 text-sm flex items-center gap-2 mt-2"><Check className="w-4 h-4" /> Success!</span>}
								{loginState.error && <span className="text-red-700 text-sm flex items-center gap-2 mt-2"><X className="w-4 h-4" /> {loginState.error}</span>}
							</div>
						</form>
					</Form>
				</div>
				<div className="text-[11px] text-gray-500 mt-2 flex-1">
					By signing up, you acknowledge that you have read, understood, and agree to the BetterCampus <a href="/terms" target="_blank" className="text-blue-500">Terms</a> and <a href="/privacy" target="_blank" className="text-blue-500">Privacy Policy</a>
				</div>
			</div>
		</GoogleOAuthProvider>
	)
}

export default GoogleAuth;
